/**
 * useOrgCacheCleanup
 *
 * Watches both org ID and active business ID from auth context.
 * Clears org-scoped Zustand stores + React Query caches when either changes.
 * This prevents cross-tenant data leakage in localStorage-persisted stores.
 *
 * @module hooks/useOrgCacheCleanup
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useBatchStore } from '@/features/conciliation/store/batchStore';
import { useConciliationStore } from '@/features/conciliation/store/conciliationStore';
import { useArbitrationStore } from '@/features/arbitration/store/arbitrationStore';
import { useBusinessStore } from '@/features/business-clients/store/businessStore';
import { useCalendarStore } from '@/features/calendar/store/calendarStore';
import { logger } from '@/lib/logging';

export function useOrgCacheCleanup() {
  const { orgId, activeBusinessId } = useAuth();
  const queryClient = useQueryClient();
  const prevOrgIdRef = useRef<string | null>(null);
  const prevBusinessIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId) return;

    const prevOrgId = prevOrgIdRef.current;
    const prevBusinessId = prevBusinessIdRef.current;

    // First mount — just set org/business IDs on stores, no clear needed
    if (prevOrgId === null) {
      prevOrgIdRef.current = orgId;
      prevBusinessIdRef.current = activeBusinessId;
      useBatchStore.getState().setOrgId(orgId);
      useConciliationStore.getState().setOrgId(orgId);
      if (activeBusinessId) {
        useBatchStore.getState().setBusinessId(activeBusinessId);
        useConciliationStore.getState().setBusinessId(activeBusinessId);
      }
      return;
    }

    const orgChanged = prevOrgId !== orgId;
    // Detect real business switch: both old and new must be non-null values
    // (skip null→value transitions which are just initial assignment after auth loads)
    const businessChanged = prevBusinessId !== activeBusinessId
      && prevBusinessId !== null
      && activeBusinessId !== null;

    if (orgChanged || businessChanged) {
      logger.info('CACHE_CLEANUP', {
        reason: orgChanged ? 'org_switch' : 'business_switch',
        fromOrg: prevOrgId, toOrg: orgId,
        fromBusiness: prevBusinessId, toBusiness: activeBusinessId,
      });

      // Clear all business-scoped Zustand stores
      useBatchStore.getState().clearForOrgSwitch();
      useConciliationStore.getState().clearForOrgSwitch();
      useArbitrationStore.getState().clearForOrgSwitch();
      useBusinessStore.getState().clearForOrgSwitch();
      useCalendarStore.getState().clearForOrgSwitch();

      // Set new org/business IDs on stores
      useBatchStore.getState().setOrgId(orgId);
      useConciliationStore.getState().setOrgId(orgId);
      if (activeBusinessId) {
        useBatchStore.getState().setBusinessId(activeBusinessId);
        useConciliationStore.getState().setBusinessId(activeBusinessId);
      }

      // Clear entire React Query cache to prevent cross-business data leakage
      queryClient.clear();
    }

    // Always track the latest values so subsequent changes are detected.
    // Previously this was inside the if-block, which meant null→value transitions
    // left prevBusinessIdRef stuck at null, breaking all future switch detection.
    prevOrgIdRef.current = orgId;
    prevBusinessIdRef.current = activeBusinessId;
  }, [orgId, activeBusinessId, queryClient]);
}
